import React, { useState, useRef } from 'react';
import { createBooking } from '../../api';
import { ClipLoader } from 'react-spinners';

function CreateBlocking({ professionals }) {
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedHour, setSelectedHour] = useState('');
  const [selectedMinute, setSelectedMinute] = useState('');
  const [selectedDuration, setSelectedDuration] = useState('60');
  const [observation, setObservation] = useState('');
  const [isWholeDay, setIsWholeDay] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showMissingFieldsModal, setShowMissingFieldsModal] = useState(false);
  const [missingFields, setMissingFields] = useState([]);
  
  const dateInputRef = useRef(null);

  const checkMissingFields = () => {
    const missing = [];
    if (!selectedProfessional) missing.push('Profissional');
    if (!selectedDate) missing.push('Data');
    if (!isWholeDay && (!selectedHour || !selectedMinute)) missing.push('Horário');
    if (!isWholeDay && !selectedDuration) missing.push('Duração');
    return missing;
  };

  const handleCreateBlocking = async () => {
    const missingFieldsList = checkMissingFields();
    
    if (missingFieldsList.length > 0) {
      setMissingFields(missingFieldsList);
      setShowMissingFieldsModal(true);
      setTimeout(() => setShowMissingFieldsModal(false), 3000);
      return;
    }
    
    let formattedTime;
    let duration;
    
    if (isWholeDay) {
      formattedTime = '07:00';
      duration = 660;
    } else {
      formattedTime = `${selectedHour.padStart(2, '0')}:${selectedMinute.padStart(2, '0')}`;
      duration = parseInt(selectedDuration);
    }
    
    const blockingData = {
      professionalId: selectedProfessional.id,
      dataAtendimento: `${selectedDate}T${formattedTime}:00Z`,
      duration: duration,
      observacao: observation,
      status: 'Bloqueio'
    };

    try {
      setIsLoading(true);
      await createBooking(blockingData);
      setSuccessMessage('Bloqueio criado com sucesso!');
      resetForm();
    } catch (error) {
      console.error('Blocking creation failed:', error);
      setSuccessMessage(`Erro ao criar bloqueio: ${error.message || 'Por favor, tente novamente.'}`);
    } finally {
      setIsLoading(false);
      setTimeout(() => setSuccessMessage(''), 5000);
    }
  };

  const resetForm = () => {
    setSelectedProfessional(null);
    setSelectedDate(null);
    setSelectedHour('');
    setSelectedMinute('');
    setSelectedDuration('60');
    setObservation('');
    setIsWholeDay(false);
    
    const dateInput = dateInputRef.current;
    if (dateInput) {
      dateInput.value = '';
    }
  };

  const MissingFieldsModal = () => {
    if (!showMissingFieldsModal) return null;

    return (
      <div className="modal-overlay" onClick={() => setShowMissingFieldsModal(false)}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <button className="close-button" onClick={() => setShowMissingFieldsModal(false)}>×</button>
          <h3>Campos Obrigatórios</h3>
          <p>Por favor, preencha os campos a seguir para criar o bloqueio:</p>
          <ul>
            {missingFields.map((field, index) => (
              <li key={index}>{field}</li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  return (
    <>
      <h2 style={{ color: '#4a90e2', marginBottom: '1rem' }}>Bloqueios de Agenda</h2>
      <p style={{ color: '#888', marginBottom: '2rem' }}>
        Crie bloqueios para impedir agendamentos em horários específicos para um profissional.
      </p>
      
      <div className="booking-grid">
        <div className="booking-section">
          <h3>Escolha o profissional <span style={{color: 'red'}}>*</span></h3>
          <div className="search-status">
            <div className="status-multiselect">
              <button
                type="button"
                className="status-trigger"
                onClick={(e) => {
                  const dropdown = e.currentTarget.nextSibling;
                  if (dropdown) dropdown.style.display = dropdown.style.display === 'block' ? 'none' : 'block';
                }}
              >
                {selectedProfessional?.name || 'Selecionar profissional'}
              </button>
              <div className="status-dropdown" style={{ display: 'none' }}>
                {professionals.map((prof) => (
                  <div
                    key={prof.id}
                    className="status-option"
                    onClick={(e) => {
                      setSelectedProfessional(prof);
                      const dropdown = e.currentTarget.parentElement;
                      if (dropdown && dropdown.classList.contains('status-dropdown')) {
                        dropdown.style.display = 'none';
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <span>{prof.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="booking-section">
          <h3>Escolha a data <span style={{color: 'red'}}>*</span></h3>
          <div className="date-time-container">
            <div 
              className="date-input-container"
              onClick={() => {
                if (dateInputRef.current && typeof dateInputRef.current.showPicker === 'function') {
                  dateInputRef.current.showPicker();
                } else if (dateInputRef.current) {
                  dateInputRef.current.focus();
                  dateInputRef.current.click();
                }
              }}
            >
              <input 
                type="date" 
                className="date-input"
                ref={dateInputRef}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="booking-section">
          <h3>Dia inteiro?</h3>
          <label className="whole-day-checkbox">
            <input
              type="checkbox"
              checked={isWholeDay}
              onChange={(e) => setIsWholeDay(e.target.checked)}
            />
            <span>Bloquear o dia inteiro (07:00 - 18:00)</span>
          </label>
        </div>

        {!isWholeDay && (
          <>
            <div className="booking-section">
              <h3>Escolha o horário <span style={{color: 'red'}}>*</span></h3>
              <div className="time-grid">
                <div className="time-selection-container">
                  <div className="time-column">
                    <h4>Hora</h4>
                    <div className="time-scroll">
                      {[...Array(15)].map((_, i) => (
                        <div 
                          key={i} 
                          className={`time-item ${selectedHour === (i + 8).toString() ? 'selected' : ''}`}
                          onClick={() => setSelectedHour((i + 8).toString())}
                        >
                          {(i + 8).toString().padStart(2, '0')}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="time-column">
                    <h4>Minuto</h4>
                    <div className="time-scroll">
                      {[...Array(12)].map((_, i) => (
                        <div 
                          key={i} 
                          className={`time-item ${selectedMinute === (i * 5).toString() ? 'selected' : ''}`}
                          onClick={() => setSelectedMinute((i * 5).toString())}
                        >
                          {(i * 5).toString().padStart(2, '0')}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="booking-section">
              <h3>Duração <span style={{color: 'red'}}>*</span></h3>
              <div className="duration-input">
                <label>Duração em minutos:</label>
                <input 
                  type="text"
                  inputMode="decimal"
                  className="date-input"
                  value={selectedDuration}
                  onChange={(e) => {
                    const value = e.target.value.replace(',', '.');
                    setSelectedDuration(value);
                  }}
                  placeholder="Tempo de duração"
                />
              </div>
            </div>
          </>
        )}

        <div className="booking-section">
          <h3>Motivo do bloqueio (opcional)</h3>
          <textarea 
            className="date-input"
            value={observation}
            onChange={(e) => setObservation(e.target.value)}
            placeholder="Ex: Férias, Reunião, Consulta médica..."
            rows="4"
          />
        </div>
      </div>

      <button 
        className="book-button"
        onClick={handleCreateBlocking}
      >
        {isLoading ? <ClipLoader size={20} color={"#ffffff"} /> : 'Criar Bloqueio'}
      </button>

      {successMessage && (
        <div className={`success-message ${successMessage.includes('Erro') ? 'error' : 'success'}`}>
          {successMessage}
        </div>
      )}
      
      <MissingFieldsModal />
    </>
  );
}

export default CreateBlocking;

